import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { PageHeader, Card, Button, Input, Select, Label, EmptyState } from "../components/ui-kit";
import { Plus, Trash2, Upload, Download, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { email: "", password: "", name: "", role: "sales", phone: "", team_code: "", reports_to: "", rate_per_km: "", primary_price_list_id: "", secondary_price_list_id: "" };

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    const [u, p] = await Promise.all([api.get("/users"), api.get("/price-lists")]);
    setRows(u.data);
    setPriceLists(p.data);
  };

  // Memoised dropdown options — recomputed only when data or the row being edited changes.
  const reportsToOptions = useMemo(
    () => rows.filter((u) => ["admin", "director", "manager", "rm_and_above", "sales_manager"].includes(u.role) && u.id !== editing?.id),
    [rows, editing?.id]
  );
  const secondaryPriceListOptions = useMemo(
    () => priceLists.filter((pl) => pl.id !== form.primary_price_list_id),
    [priceLists, form.primary_price_list_id]
  );
  useEffect(() => {
    load();
    // Initial fetch only on mount; `load` is a stable closure over module-level `api`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email || "",
      password: "",  // blank; only sent if user types a new one
      name: u.name || "",
      role: u.role || "sales",
      phone: u.phone || "",
      team_code: u.team_code || "",
      reports_to: u.reports_to || "",
      rate_per_km: u.rate_per_km != null ? String(u.rate_per_km) : "",
      primary_price_list_id: u.primary_price_list_id || "",
      secondary_price_list_id: u.secondary_price_list_id || "",
    });
    setOpen(true);
  };

  const closeForm = () => {
    setOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const updates = {
          name: form.name,
          phone: form.phone,
          role: form.role,
          username: form.email,
          team_code: (form.team_code || "").toUpperCase(),
          reports_to: form.reports_to || "",
          rate_per_km: form.rate_per_km === "" ? 0 : Number(form.rate_per_km),
          primary_price_list_id: form.primary_price_list_id || "",
          secondary_price_list_id: form.secondary_price_list_id || "",
        };
        // Only send password if a new one was typed (blank = leave unchanged).
        if (form.password) updates.password = form.password;
        await api.put(`/users/${editing.id}`, updates);
        toast.success("User updated");
      } else {
        const password = form.password || form.phone;
        if (!password) {
          toast.error("Provide either a phone number (used as password) or a password");
          return;
        }
        await api.post("/users", {
          email: form.email,
          password,
          name: form.name,
          role: form.role,
          phone: form.phone,
          username: form.email,
          team_code: (form.team_code || "").toUpperCase(),
          reports_to: form.reports_to || "",
          rate_per_km: form.rate_per_km === "" ? 0 : Number(form.rate_per_km),
          primary_price_list_id: form.primary_price_list_id || "",
          secondary_price_list_id: form.secondary_price_list_id || "",
        });
        toast.success("User created");
      }
      closeForm();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("download failed:", err);
      toast.error("Download failed");
    }
  };

  const uploadExcel = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/users/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported — created ${data.created}, updated ${data.updated}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} rows had issues`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Team"
        subtitle="Email is the username · Phone number is the default password"
        actions={
          <>
            <Button variant="outline" onClick={() => download("/users/template.xlsx", "sales_personnel_template.xlsx")} data-testid="users-template-btn">
              <Download className="w-4 h-4" /> Template
            </Button>
            <label className="inline-flex items-center gap-2 cursor-pointer bg-transparent text-[#1E3F20] border border-[#1E3F20] hover:bg-[#F2EDE4] rounded-lg px-4 py-2 font-semibold text-sm transition-colors">
              <Upload className="w-4 h-4" /> Upload Excel
              <input
                type="file"
                accept=".xlsx"
                className="hidden"
                data-testid="users-upload"
                onChange={(e) => e.target.files?.[0] && uploadExcel(e.target.files[0])}
              />
            </label>
            <Button onClick={openNew} data-testid="new-user-btn">
              <Plus className="w-4 h-4" /> New user
            </Button>
          </>
        }
      />
      {open && (
        <Card className="mb-6">
          <h3 className="font-heading text-xl font-bold mb-4">{editing ? "Edit user" : "New user"}</h3>
          <p className="text-sm text-[#5C6B5E] mb-4">
            {editing
              ? "Update the user's details. Leave password blank to keep the current password."
              : "Email is used as the login & username. If you leave password blank, the phone number is used as the password."}
          </p>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
            </div>
            <div>
              <Label>Email (also used as username)</Label>
              <Input
                type="email"
                required
                value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-email"
              />
              {editing && <div className="text-[11px] text-[#5C6B5E] mt-1">Email cannot be changed. Delete and re-create to use a different one.</div>}
            </div>
            <div>
              <Label>Phone number{editing ? "" : " (used as password if left blank below)"}</Label>
              <Input required={!editing} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-phone" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                <option value="admin">Admin</option>
                <option value="director">Director</option>
                <option value="manager">Manager</option>
                <option value="rm_and_above">RM &amp; Above</option>
                <option value="sales_manager">Sales Manager</option>
                <option value="sales">Sales Personnel</option>
                <option value="hr">HR</option>
              </Select>
            </div>
            <div>
              <Label>Team code (2 letters — used for order # prefix)</Label>
              <Input
                value={form.team_code}
                onChange={(e) => setForm({ ...form, team_code: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="e.g. RK"
                maxLength={2}
                data-testid="user-team-code"
              />
            </div>
            <div>
              <Label>Reports to (optional)</Label>
              <Select
                value={form.reports_to}
                onChange={(e) => setForm({ ...form, reports_to: e.target.value })}
                data-testid="user-reports-to"
              >
                <option value="">— None —</option>
                {reportsToOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.role})
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Rate per km (₹) — used to auto-compute travel expenses</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.rate_per_km}
                onChange={(e) => setForm({ ...form, rate_per_km: e.target.value })}
                placeholder="e.g. 5"
                data-testid="user-rate-per-km"
              />
            </div>
            <div>
              <Label>Primary price list</Label>
              <Select
                value={form.primary_price_list_id}
                onChange={(e) => setForm({ ...form, primary_price_list_id: e.target.value })}
                data-testid="user-primary-pl"
              >
                <option value="">— None —</option>
                {priceLists.map((pl) => (
                  <option key={pl.id} value={pl.id}>{pl.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Secondary price list</Label>
              <Select
                value={form.secondary_price_list_id}
                onChange={(e) => setForm({ ...form, secondary_price_list_id: e.target.value })}
                data-testid="user-secondary-pl"
              >
                <option value="">— None —</option>
                {secondaryPriceListOptions.map((pl) => (
                  <option key={pl.id} value={pl.id}>{pl.name}</option>
                ))}
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>{editing ? "New password (optional — leave blank to keep current)" : "Password (optional — defaults to phone number)"}</Label>
              <Input
                type="password"
                placeholder={editing ? "Leave blank to keep current password" : "Leave blank to use phone number"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="user-password"
              />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={closeForm}>
                Cancel
              </Button>
              <Button type="submit" data-testid="user-submit">
                {editing ? "Save changes" : "Create user"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No users yet" description="Add a user, or upload the Sales Personnel Excel sheet to bulk-import." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email / Username</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Team code</th>
                  <th className="px-4 py-3">Rate/km</th>
                  <th className="px-4 py-3">Reports to</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60">
                    <td className="px-4 py-3 font-semibold">{r.name}</td>
                    <td className="px-4 py-3">{r.email}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.phone}</td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-[#A3B18A]/20 text-[#1E3F20]">
                        {(r.role || "").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs" data-testid={`user-team-code-${r.id}`}>{r.team_code || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs" data-testid={`user-rate-${r.id}`}>{r.rate_per_km ? `₹${Number(r.rate_per_km).toFixed(2)}` : "—"}</td>
                    <td className="px-4 py-3 text-xs">{rows.find((x) => x.id === r.reports_to)?.name || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => openEdit(r)}
                          className="text-xs px-2.5 py-1 rounded bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 font-semibold inline-flex items-center gap-1"
                          data-testid={`edit-user-${r.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button onClick={() => del(r.id)} className="text-red-600 p-1.5 hover:bg-red-50 rounded" data-testid={`delete-user-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
